import type { TrackEventInput } from '__generated__/graphql'
import type {
  AnalyticsClient,
  AnalyticsClientDeps,
  AnalyticsPrerequisite,
  AnalyticsTask
} from './analytics-client'
import type { AnalyticsEventName, AnalyticsEventProps } from './events'
import { FlushTimer } from './flush-timer'
import type { AnalyticsIdentity } from './identity'
import { PrerequisiteTasks } from './prerequisite-tasks'
import {
  ANALYTICS_LIMITS,
  cutToBytes,
  sanitizeProps,
  trimText
} from './sanitize'
import { settleSafely } from './transport'
import type { SendOptions } from './transport'

/** The oldest events go first beyond it (no identity yet, backend away). */
const MAX_QUEUED_EVENTS = 200
const SEND_DELAY_MS = 5000
/** The gateway takes JSON bodies up to 100 KB; the rest is the query. */
const BATCH_BYTES = 90 * 1024
/** Browsers keep ≤ 64 KB of keepalive bodies in flight. */
const KEEPALIVE_BATCH_BYTES = 60 * 1024

/**
 * The `AnalyticsClient` of `createAnalyticsClient` (FE-321). A batch goes out
 * only once no prerequisite is pending, so a task registered while the queue
 * drains — the consent of an identity that has just been set — still goes
 * before it.
 */
export class AnalyticsQueue implements AnalyticsClient {
  private queue: TrackEventInput[] = []
  /** Failed attempts of the events that went back to the queue. */
  private readonly eventFailures = new Map<string, number>()
  private identity: AnalyticsIdentity | null = null
  private consentDecided = false
  private flushing: Promise<void> | null = null
  private readonly timer = new FlushTimer(() => void this.flush())
  private readonly tasks = new PrerequisiteTasks(this.timer)

  constructor(private readonly deps: AnalyticsClientDeps) {
    deps.listenPageHide(() => void this.flush({ keepalive: true }))
  }

  track = <N extends AnalyticsEventName>(
    name: N,
    props: AnalyticsEventProps[N]
  ) => {
    const clean = sanitizeProps(props)
    if (!clean) {
      console.warn(`analytics: ${name} dropped, props over 4 KB`)
      return
    }

    this.queue.push({
      eventId: this.deps.randomId(),
      name: trimText(name, ANALYTICS_LIMITS.nameLength),
      occurredAt: new Date().toISOString(),
      props: clean,
      page: this.deps.readPage()
    })
    this.capQueue()
    this.schedule()
  }

  setIdentity = (identity: AnalyticsIdentity | null) => {
    this.identity = identity
    this.schedule()
  }

  setConsentDecided = (decided: boolean) => {
    this.consentDecided = decided
    this.schedule()
  }

  runBefore = (kind: AnalyticsPrerequisite, task: AnalyticsTask) => {
    this.tasks.set(kind, task)
    this.schedule()
  }

  flush = (options: SendOptions = {}): Promise<void> => {
    if (options.keepalive) return this.flushOnHide()

    this.flushing ??= this.drain().finally(() => {
      this.flushing = null
      this.schedule()
    })
    return this.flushing
  }

  private get hasFullBatch() {
    return this.queue.length >= ANALYTICS_LIMITS.eventsPerBatch
  }

  /** Prerequisites right away; events at 50, else 5 s after a consent answer. */
  private schedule() {
    if (!this.identity || this.flushing || this.timer.isBackingOff) return

    if (this.tasks.size || this.hasFullBatch) this.timer.start(0)
    else if (this.consentDecided && this.queue.length) {
      this.timer.start(SEND_DELAY_MS)
    }
  }

  private async drain() {
    while (this.identity && (await this.tasks.runAll())) {
      if (!this.queue.length || !(this.consentDecided || this.hasFullBatch)) {
        return
      }

      if (!(await this.sendBatch(this.takeBatch(BATCH_BYTES), {}))) return
    }
  }

  /** Up to 50 leading events whose JSON fits into `maxBytes`. */
  private takeBatch(maxBytes: number) {
    const leading = this.queue.slice(0, ANALYTICS_LIMITS.eventsPerBatch)
    return this.queue.splice(0, cutToBytes(leading, maxBytes).length)
  }

  /** `true` once answered or given up; `false` while a retry waits. */
  private async sendBatch(batch: TrackEventInput[], options: SendOptions) {
    const result = await settleSafely(() =>
      this.deps.sendEvents(batch, options)
    )
    const failures = result === 'retry' ? this.failuresOf(batch) + 1 : 0

    if (failures && this.timer.retry(failures, `${batch.length} events`)) {
      batch.forEach(event => this.eventFailures.set(event.eventId, failures))
      // NOTE: the same eventIds on the retry, so the backend deduplicates them
      this.queue.unshift(...batch)
      this.capQueue()
      return false
    }

    batch.forEach(event => this.eventFailures.delete(event.eventId))
    return true
  }

  /** The most failed attempts among the events of a batch. */
  private failuresOf(batch: TrackEventInput[]) {
    const failures = batch.map(event => this.eventFailures.get(event.eventId))
    return Math.max(0, ...failures.map(count => count ?? 0))
  }

  private capQueue() {
    const excess = this.queue.length - MAX_QUEUED_EVENTS
    if (excess <= 0) return

    this.queue
      .splice(0, excess)
      .forEach(event => this.eventFailures.delete(event.eventId))
  }

  /**
   * The page may be gone before any answer, so every call starts in this
   * tick without waiting for a consent answer, a running flush or a backoff:
   * the pending tasks and one batch cut to the keepalive budget. They run in
   * parallel, so the backend may journal the batch before a consent sent
   * along with it.
   */
  private async flushOnHide() {
    if (!this.identity) return

    const options = { keepalive: true }
    const tasks = this.tasks.startAll(options)
    const batch = this.takeBatch(KEEPALIVE_BATCH_BYTES)

    await Promise.all(
      batch.length ? [...tasks, this.sendBatch(batch, options)] : tasks
    )
  }
}
